import { Injectable } from '@angular/core';
import { catchError, filter, from, map, Observable, of, switchMap, tap, timer } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { Big } from 'big.js';
import { DataStorage } from '../../utils';

export interface TokenPricesUSD {
  sol: number;
  neon: number;
}

// Pyth no longer publishes a NEON feed and the pythnet RPC is gone, so USD prices come from CoinGecko.
const PRICES_URL = 'https://api.coingecko.com/api/v3/simple/price?ids=solana,neon&vs_currencies=usd';
const PRICES_REFRESH_INTERVAL = 5 * 60 * 1000;

// Market prices on 2026-11-04 (CoinGecko). Used until the first successful request
// and whenever the API is unavailable or rate-limited.
export const FALLBACK_PRICES_USD: TokenPricesUSD = { sol: 92.04, neon: 0.00260343 };

@Injectable({ providedIn: 'root' })
export class PythService extends DataStorage<TokenPricesUSD | null> {
  get solPrice(): Big {
    return new Big(this.data?.sol || FALLBACK_PRICES_USD.sol);
  }

  get neonPrice(): Big {
    return new Big(this.data?.neon || FALLBACK_PRICES_USD.neon);
  }

  // NEON per 1 SOL: multiply a SOL amount by it to get the NEON amount.
  get solPerNeon(): Big {
    return this.solPrice.gt(0) && this.neonPrice.gt(0) ? this.solPrice.div(this.neonPrice) : new Big(0);
  }

  // SOL per 1 NEON.
  get neonPerSol(): Big {
    return this.solPrice.gt(0) && this.neonPrice.gt(0) ? this.neonPrice.div(this.solPrice) : new Big(0);
  }

  prices(): Observable<TokenPricesUSD | null> {
    this.loading.next(true);
    return from(fetch(PRICES_URL, { headers: { Accept: 'application/json' } }).then(r => {
      if (!r.ok) {
        throw new Error(`HTTP ${r.status}`);
      }
      return r.json();
    })).pipe(
      map(d => ({ sol: Number(d?.solana?.usd) || 0, neon: Number(d?.neon?.usd) || 0 })),
      filter(d => d.sol > 0 && d.neon > 0),
      tap(this.dataEmit),
      catchError(e => {
        console.warn(`Token prices are unavailable: ${e?.message ?? e}`);
        return of(null);
      }),
      finalize(this.loadingFinalize));
  }

  init() {
    this.subs.push(timer(0, PRICES_REFRESH_INTERVAL).pipe(switchMap(() => this.prices())).subscribe());
  }

  destroy() {
    super.destroy();
  }
}
